import 'react-datepicker/dist/react-datepicker.css'
import './DateRangeIndividualPicker.css'

import { ChevronUpDownIcon } from '@heroicons/react/24/outline'
import { cn } from '@sparkai/utils'
import { getMonth } from 'date-fns'
import { ComponentPropsWithoutRef, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import ReactDatePicker from 'react-datepicker'

import { DateInterval } from '../DatePicker/DatePicker'
import { DateRangeIndividualPickerHeader } from './DateRangeIndividualPickerHeader'

export type DateRangeTuple = [Date | null, Date | null]

export interface DateRangeIndividualPickerProps {
  value?: DateRangeTuple
  onChange: (date: DateRangeTuple) => void
  placeholder?: string

  showTimeSelect?: boolean
  timeIntervals?: number
  monthsShown?: number
  showTimeInput?: boolean

  excludeDates?: Date[]
  excludeDateIntervals?: DateInterval[]
  filterDate?: (date: Date) => boolean

  excludeTimes?: Date[]
  filterTime?: (date: Date) => boolean

  shouldCloseOnSelect?: boolean
  showMonthAndYearSelector?: boolean
  timeFormat?: string
  dateFormat?: string

  maxDate?: Date | null
  minDate?: Date | null

  className?: string
  daysDefaultRange: number
  error?: boolean

  hideInput?: boolean
  autoShow?: boolean
  headerClassName?: string
  calendarTheme?: 'full' | 'shadow'
  inline?: boolean
  calendarClassName?: string
}

export function DateRangeIndividualPicker({
  value,
  onChange,
  placeholder,
  daysDefaultRange,
  error,
  hideInput,
  autoShow,
  headerClassName,
  calendarTheme = 'shadow',
  calendarClassName,
  inline = false,
  ...props
}: DateRangeIndividualPickerProps) {
  const startDate = value?.[0]
  const endDate = value?.[1]
  const datePickerRef = useRef<ReactDatePicker>(null)

  const [isRangeDaysSelected, setIsRangeDaysSelected] = useState(false)
  const [focusSelectedMonth, setFocusSelectedMonth] = useState(false)

  const todayMonthNumber = getMonth(new Date()) + 1
  const startMonthNumber = startDate ? getMonth(startDate) + 1 : null
  const endMonthNumber = endDate ? getMonth(endDate) + 1 : null

  useEffect(() => {
    if (startMonthNumber !== todayMonthNumber && startMonthNumber !== null && endDate === null) {
      setFocusSelectedMonth(true)
    }
  }, [startMonthNumber, todayMonthNumber, endDate])

  return (
    <div className='date-range-individual-picker-root'>
      <ReactDatePicker
        inline={inline}
        ref={datePickerRef}
        selected={startDate}
        showPopperArrow={false}
        customInput={
          hideInput ? (
            <></>
          ) : (
            <CustomInputSelect
              error={error}
              rangeDays={daysDefaultRange}
              isRangeDaysSelected={isRangeDaysSelected}
            />
          )
        }
        calendarClassName={cn(
          {
            'w-full border-0 mt-[-20px]': calendarTheme === 'full',
            'shadow-xl ring-1 ring-black ring-opacity-5 p-6 border-0 rounded-md': calendarTheme === 'shadow',
          },
          calendarClassName
        )}
        onChange={(update) => {
          if (onChange) {
            const date = update as unknown as DateRangeTuple
            setIsRangeDaysSelected(false)

            const startDate = date?.[0]
            const endDate = date?.[1]

            if (startDate && endDate) setFocusSelectedMonth(false)

            onChange(date)
          }
        }}
        startDate={startDate}
        renderCustomHeader={(props) => (
          <DateRangeIndividualPickerHeader
            className={headerClassName}
            onClickRangeDates={(value) => {
              const date = value as unknown as DateRangeTuple
              setIsRangeDaysSelected(true)
              onChange && onChange(date)
              !autoShow && datePickerRef?.current?.setOpen(false)

              const startDate = date?.[0]
              const endDate = date?.[1]

              if (startDate && endDate) setFocusSelectedMonth(false)
            }}
            rangeDaysSelected={isRangeDaysSelected}
            rangeDays={daysDefaultRange}
            startDate={startDate}
            endDate={endDate}
            {...props}
          />
        )}
        monthsShown={2}
        showPreviousMonths={
          (!startDate && !endDate) ||
          (autoShow && todayMonthNumber === startMonthNumber && todayMonthNumber === endMonthNumber)
        }
        focusSelectedMonth={focusSelectedMonth}
        endDate={endDate}
        placeholderText={placeholder}
        selectsRange
        open={autoShow}
        onCalendarClose={() => {
          setFocusSelectedMonth(false)
        }}
        {...props}
      />
    </div>
  )
}

export type CustomInputSelectProps = ComponentPropsWithoutRef<'button'> & {
  isRangeDaysSelected?: boolean
  rangeDays: number
  error?: boolean
}

const CustomInputSelect = forwardRef<HTMLButtonElement, CustomInputSelectProps>(function CustomInputSelect(
  { value, isRangeDaysSelected, error, rangeDays, ...props },
  ref
) {
  const innerRef = useRef<HTMLButtonElement>(null)
  // Just a trick to use ref while also forwarding
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  useImperativeHandle(ref, () => innerRef.current!)

  return (
    <div
      className={cn(
        'mt-1 flex w-full cursor-pointer items-center justify-between rounded-md border border-brand-gray-300 bg-white px-3 py-2 font-inter text-sm text-brand-gray-900 shadow-sm',
        {
          'border border-brand-red-400': error,
        }
      )}
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        innerRef.current?.focus()
      }}
      aria-label='open calendar modal'
    >
      <button ref={innerRef} {...props}>
        {value}
        {isRangeDaysSelected && <span className='ml-1 text-brand-gray-500'>(Last {rangeDays} Days)</span>}
      </button>

      <ChevronUpDownIcon className='h-5 w-5 font-bold text-brand-gray-400' aria-hidden='true' />
    </div>
  )
})
